<script setup lang="ts">
import AppButton from '@/shared/ui/AppButton/index';
import ChiefEditorCardTopics from '@/widgets/ChiefEditorCard/ui/ChiefEditorCardTopics.vue';
import { DEFAULT_USER_IMAGE } from '@/shared/constants/userConstants';
import { ROOT_EDITORS_STAFF_URL } from '@/shared/constants/urlConstants';
import type { IChiefEditor } from '@/entities/journal/model/types';

const props = withDefaults(
  defineProps<{
    data?: Partial<IChiefEditor>;
    isSeoTitle?: boolean;
    isEditorDetailPage?: boolean;
  }>(),
  {
    data: () => ({}),
    isSeoTitle: false,
    isEditorDetailPage: false,
  },
);

const isMobile: boolean = useIsMobile();

const titleTag: ComputedRef<string> = computed(() => (props.isSeoTitle ? 'h1' : 'div'));

const editorProfileLink = `${ROOT_EDITORS_STAFF_URL}igor-lesovskih/`;
</script>

<template>
  <div
    :class="['chief-editor-card', { _mobile: isMobile }]"
    data-testid="chief-editor-card"
  >
    <div class="chief-editor-card__top">
      <div class="chief-editor-card__avatar-wrap">
        <NuxtImg
          class="chief-editor-card__avatar"
          :src="data.urlAvatar || DEFAULT_USER_IMAGE"
          :alt="data.full_name"
          width="140"
          height="140"
          densities="x1 x2"
          loading="lazy"
        />
      </div>
      <div v-if="isMobile && data.contacts" class="chief-editor-card__contacts">
        <a
          v-if="data.contacts.email"
          class="chief-editor-card__contact"
          :href="`mailto:${data.contacts.email}`"
        >
          <AppIcon class="chief-editor-card__contact-icon _mail" name="Mail" />
          {{ data.contacts.email }}
        </a>
        <a
          v-if="data.contacts.telegram"
          class="chief-editor-card__contact"
          :href="data.contacts.telegram"
          target="_blank"
          rel="noopener noreferrer"
        >
          <AppIcon
            class="chief-editor-card__contact-icon _telegram"
            name="TelegramNotRounded"
          />
          Telegram
        </a>
        <a
          v-if="data.contacts.vk"
          class="chief-editor-card__contact"
          :href="data.contacts.vk"
          target="_blank"
          rel="noopener noreferrer"
        >
          <AppIcon class="chief-editor-card__contact-icon _vk" name="VkNotRounded" />
          VK
        </a>
      </div>
    </div>
    <div class="chief-editor-card__info">
      <div class="chief-editor-card__heading">
        <component :is="titleTag" class="chief-editor-card__name">
          {{ data.full_name }}
        </component>
        <div v-if="data.position" class="chief-editor-card__job-title">
          {{ data.position }}
        </div>
      </div>
      <div v-if="data.bio_short" class="chief-editor-card__description">
        {{ data.bio_short }}
      </div>
      <ChiefEditorCardTopics
        v-if="data.topics && data.topics.length"
        class="chief-editor-card__categories"
        :topics="data.topics"
      />
      <div class="chief-editor-card__footer">
        <AppButton
          v-if="!isEditorDetailPage"
          class="chief-editor-card__button"
          data-testid="chief-editor-card-profile-button"
          is-bordered
          is-white
          button-text="Подробнее об авторе"
          right-icon-name="ArrowRight"
          right-icon-width="12"
          right-icon-height="12"
          :to="editorProfileLink"
        />
        <div v-if="!isMobile && data.contacts" class="chief-editor-card__contacts">
          <a
            v-if="data.contacts.email"
            class="chief-editor-card__contact"
            :href="`mailto:${data.contacts.email}`"
          >
            <AppIcon class="chief-editor-card__contact-icon _mail" name="Mail" />
            {{ data.contacts.email }}
          </a>
          <a
            v-if="data.contacts.telegram"
            class="chief-editor-card__contact"
            :href="data.contacts.telegram"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppIcon
              class="chief-editor-card__contact-icon _telegram"
              name="TelegramNotRounded"
            />
            Telegram
          </a>
          <a
            v-if="data.contacts.vk"
            class="chief-editor-card__contact"
            :href="data.contacts.vk"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppIcon class="chief-editor-card__contact-icon _vk" name="VkNotRounded" />
            VK
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.chief-editor-card {
  .box-shadow();

  display: flex;
  gap: @size-x9;
  align-items: flex-start;
  border-radius: @main-size;
  padding: @size-x8;

  &__top {
    flex-shrink: 0;
  }

  &__avatar-wrap {
    flex-shrink: 0;
  }

  &__avatar {
    display: block;
    width: 140px;
    height: 140px;
    border-radius: @main-size;
    object-fit: cover;
  }

  &__info {
    .flex-column(@size-x4);

    flex: 1 1 auto;
    min-width: 0;
  }

  &__heading {
    .flex-column(@main-size);
  }

  &__name {
    font-size: @size-x6;
    line-height: 130%;
    font-weight: 700;
    word-break: break-word;
  }

  &__job-title {
    font-size: 14px;
    line-height: 130%;
    font-weight: 500;
    color: @color-silver-chalice;
    word-break: break-word;
  }

  &__description {
    font-size: @size-x4;
    line-height: 130%;
    font-weight: 500;
    word-break: break-word;
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: @size-x8 60px;
    margin-top: @size-x3;
  }

  &__button.app-button {
    width: 230px;
  }

  &__contacts {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: @size-x6;
  }

  &__contact {
    .color-change-hover();

    display: inline-flex;
    align-items: center;
    gap: @size-x2;
    font-size: 14px;
    line-height: 130%;
    font-weight: 500;
    word-break: break-word;
  }

  &__contact-icon {
    width: @size-x4;
    height: @size-x4;

    &._mail path {
      stroke: @color-persimmon;
    }

    &._telegram path {
      fill: @color-persimmon;
    }

    &._vk path {
      fill: @color-persimmon;
      stroke: @color-persimmon;
    }
  }

  &._mobile {
    flex-direction: column;
    gap: @size-x5;
    padding: @size-x4;
  }

  &._mobile &__top {
    display: flex;
    gap: @size-x6;
  }

  &._mobile &__avatar {
    width: 120px;
    height: 120px;
  }

  &._mobile &__contacts {
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
  }

  &._mobile &__button.app-button {
    width: 100%;
  }
}
</style>
